import type {
  ClientAddress,
  ClientGPSLocation
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, StyleSheet, View } from 'react-native';
import { TextInput } from 'react-native-paper';

import { MoveIcon } from '@/assets';
import { GeoSearchBar } from '@/features/geo-area/components/GeoSearchBar';
import { GeoAreaMap } from '@/features/geo-area/components/map/GeoAreaMap';
import type { GpsPosition } from '@/features/geo-area/utils/mapMessages';
import { useReverse } from '@/features/geo-coding/hooks/useReverse';
import {
  getAddressFromResult,
  getAdressString
} from '@/features/geo-coding/utils/places';
import { ChangePositionButton } from '@/features/post-editor/components/location/ChangePositionButton';
import { LocationPickerMapContent } from '@/features/post-editor/components/location/LocationPickerMapContent';
import { LocationSearchModal } from '@/features/post-editor/components/location/LocationSearchModal';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { CurrentLocationButton } from '@/shared/components/location/CurrentLocationButton';
import { getCurrentLocation } from '@/shared/utils/location';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    wrapper: { gap: 24, paddingHorizontal: 16, paddingBottom: 32 },
    map: {
      backgroundColor: theme.colors.surfaceContainerLow,
      borderRadius: 12
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 16
    },
    headerRow: {
      flexDirection: 'row',
      padding: 12,
      alignItems: 'center',
      justifyContent: 'center'
    },
    floatingIcon: {
      margin: 'auto',
      zIndex: 1,
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: [{ translateX: '-50%' }, { translateY: '-50%' }]
    }
  });

type LocationPickerProps = {
  defaultPosition: ClientGPSLocation | undefined;
  selectedLocation: ClientAddress | undefined;
  setSelectedLocation: Dispatch<SetStateAction<ClientAddress | undefined>>;
};

export const LocationPicker: FC<LocationPickerProps> = ({
  defaultPosition,
  selectedLocation,
  setSelectedLocation
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'screens.post-editor.locationPicker'
  });

  const [position, setPosition] = useState<ClientGPSLocation | undefined>();
  const [addMarker, setAddMarker] = useState<boolean>(
    defaultPosition == undefined
  );
  const [customName, setCustomName] = useState<string | undefined>();
  const [geoCodePosition, setGeoCodePosition] = useState<
    ClientGPSLocation | undefined
  >();

  // set initial position
  useEffect(() => {
    if (position) return;
    if (selectedLocation?.gpsLocation) {
      setPosition(selectedLocation.gpsLocation);
      setCustomName(selectedLocation.name);
    } else if (defaultPosition) {
      setPosition(defaultPosition);
    }
  }, [selectedLocation, defaultPosition, position]);

  // #region GPS-Location
  const handleGetCurrentLocation = useCallback(async () => {
    const location = await getCurrentLocation();
    setPosition(location);
    setGeoCodePosition(location);
    setAddMarker(true);
  }, []);
  // #endregion

  // custom name
  const handleChangeName = (name: string) => {
    setCustomName(name);
    if (selectedLocation) {
      setSelectedLocation({ ...selectedLocation, name });
    }
  };

  // #region geocoding

  const { data: reverseResponse, isLoading } = useReverse(
    geoCodePosition?.latitude,
    geoCodePosition?.longitude
  );

  useEffect(() => {
    if (!reverseResponse) {
      return;
    }
    const address = getAddressFromResult(reverseResponse);

    if (isEmptyOrNull(customName)) {
      setCustomName(address.name);
    } else {
      address.name = customName;
    }
    setSelectedLocation(address);
  }, [customName, reverseResponse, setSelectedLocation]);

  // #endregion geocoding

  // #region selection mode
  const [canSelectOnMap, setCanSelectOnMap] = useState(false);

  const handleChangePostion = useCallback(() => {
    setCanSelectOnMap(!canSelectOnMap);
    setPosition(geoCodePosition);
    setAddMarker(true);
  }, [canSelectOnMap, geoCodePosition]);

  const onMoveEnd = useCallback(
    ({ lat, lng }: GpsPosition) => {
      if (!canSelectOnMap) return;
      setGeoCodePosition({ latitude: lat, longitude: lng });
    },
    [canSelectOnMap]
  );
  // #endregion selection mode

  // Clears states
  const handleClear = useCallback(() => {
    setSelectedLocation(undefined);
  }, [setSelectedLocation]);

  // #region search
  const [showSearchModal, setShowSearchModal] = useState(false);
  // #endregion search

  // map dimensions
  const windowWidth = useMemo(() => Dimensions.get('window').width, []);
  const windowHeight = useMemo(() => Dimensions.get('window').height, []);
  // map height = map width
  const mapHeight = useMemo(() => windowWidth - 32, [windowWidth]);
  const mapMaxHeight = useMemo(() => windowHeight / 2, [windowHeight]);

  const handleSelectPosition = useCallback(
    (selectedPosition: ClientGPSLocation) => {
      setPosition(selectedPosition);
      setGeoCodePosition(selectedPosition);
      setAddMarker(true);
    },
    []
  );

  return (
    <View style={styles.wrapper}>
      <GeoSearchBar
        label={getAdressString(selectedLocation)}
        placeholder={t('searchButton')}
        isLoading={isLoading}
        onLocate={handleGetCurrentLocation}
        onClear={handleClear}
        onPress={() => setShowSearchModal(true)}
      />
      <View
        style={{
          height: mapHeight,
          maxHeight: mapMaxHeight,
          minHeight: 100,
          borderColor: canSelectOnMap ? theme.colors.primary : 'transparent',
          borderWidth: 3
        }}
      >
        <GeoAreaMap onMoveEnd={onMoveEnd}>
          <LocationPickerMapContent
            position={position}
            canSelect={canSelectOnMap}
            addMarker={addMarker}
          />
        </GeoAreaMap>
        <MoveIcon
          fill={canSelectOnMap ? theme.colors.primary : 'transparent'}
          height={40}
          width={40}
          style={[styles.floatingIcon, { pointerEvents: 'none' }]}
        />
      </View>
      <View style={styles.row}>
        <CurrentLocationButton
          label={t('currentLocation')}
          onPress={handleGetCurrentLocation}
        />
        <ChangePositionButton
          onPress={handleChangePostion}
          canSelectOnMap={canSelectOnMap}
        />
      </View>
      <TextInput
        mode="outlined"
        value={customName}
        onChangeText={handleChangeName}
        accessibilityLabel={t('locationNameAccessibilityLabel')}
        accessibilityHint={t('locationNameAccessibilityLabel')}
        placeholder={t('locationNamePlaceholder')}
        label={t('locationNameLabel')}
        disabled={selectedLocation === undefined}
        style={{ backgroundColor: theme.colors.surfaceContainerLow }}
      />
      <LocationSearchModal
        onSelect={handleSelectPosition}
        placeName={getAdressString(selectedLocation)}
        showSearchModal={showSearchModal}
        setShowSearchModal={setShowSearchModal}
      />
    </View>
  );
};
